<template>
  <!--    Branch Form-->
  <validation-observer
    ref="branchForm"
    v-slot="{invalid}"
  >
    <sikanii-modal
      :title="`${updateBranchMode?'Update':'Create'} Branch`"
      :show="branchModalOpened"
      :show-overlay="deletingBranch"
      size="md"
      @onClose="closeBranchModal()"
    >
      <b-row class="align-items-start">
        <div class="col-7">
          <sikanii-select
            v-model="selectedBranchId"
            placeholder="All Branches"
            :options="branches"
            label-name="name"
            value-name="id"
            name="branch"
            @input="populateBranchForm"
          />
        </div>
        <div class="col-2">
          <sikanii-dropdown
            icon="TrashIcon"
            name="delete_branch"
          >
            <b-dropdown-item
              id="cd_branch"
              @click="deleteBranch()"
            >
              Yes
            </b-dropdown-item>
            <b-dropdown-divider />
            <b-dropdown-item>
              No
            </b-dropdown-item>
          </sikanii-dropdown>
        </div>
        <div class="col-2">
          <sikanii-button
            name="branch_action"
            variant="flat-danger"
            icon="XIcon"
            label=""
            tool-tip-text="Clear Form"
            @click="resetBranchForm()"
          />
        </div>
      </b-row>
      <error-display :error="error" />
      <sikanii-input
        v-model="branch.name"
        name="name"
        placeholder="Enter branch name"
        rules="required"
        label="Branch Name"
      />
      <sikanii-input
        v-model="branch.prefix"
        name="prefix"
        placeholder="Enter Account Number Prefix"
        rules="required|max_length:15"
        label="Account Number Prefix"
      />
      <sikanii-input
        v-model="branch.length"
        name="length"
        placeholder="Enter Account Number Length"
        :rules="`required|max_value:${maxValue}`"
        label="Account Number Length"
        :readonly="!branch.prefix"
        type="number"
      />
      <template slot="modal-actions">
        <b-row class="float-right">
          <sikanii-button
            name="cancel"
            variant="flat-danger"
            icon="XIcon"
            class="mr-1"
            label="Cancel"
            @click="closeBranchModal()"
          />
          <sikanii-button
            name="save_branch"
            :label="updateBranchMode?'Update':'Create'"
            :disabled="invalid"
            :loading="branchLoading"
            loading-text="Loading..."
            @click="updateBranchMode?updateBranch():createBranch()"
          />
        </b-row>
      </template>
    </sikanii-modal>
  </validation-observer>
</template>

<script>
import {
  BRow, BDropdownItem, BDropdownDivider,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import SikaniiModal from '@/layouts/components/SikaniiModal.vue'
import SikaniiInput from '@/layouts/components/SikaniiInput.vue'
import SikaniiButton from '@/layouts/components/SikaniiButton.vue'
import showToast from '@/libs/mixins/showToast'
import SikaniiSelect from '@/layouts/components/SikaniiSelect.vue'
import SikaniiDropdown from '@/layouts/components/SikaniiDropdown.vue'
import logData from '@/libs/log'
import ErrorDisplay from '@/layouts/components/ErrorDisplay.vue'
import sexMixin from '@/libs/mixins/sexMixin'

export default {
  name: 'BranchSetup',
  components: {
    ErrorDisplay,
    SikaniiDropdown,
    SikaniiSelect,
    SikaniiButton,
    SikaniiInput,
    SikaniiModal,
    ValidationObserver,
    BRow,
    BDropdownItem,
    BDropdownDivider,
  },
  mixins: [showToast, sexMixin],
  props: {
    modalOpened: {
      type: Boolean,
      default: false,
    },
    editId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      error: {},
      branchModalOpened: false,
      updateBranchMode: false,
      branchLoading: false,
      deletingBranch: false,
      selectedBranchId: null,
      branch: {
        id: null,
        name: null,
        prefix: null,
        length: null,
      },
    }
  },
  computed: {
    maxValue() {
      let limit = 15
      if (this.branch.prefix) {
        limit -= this.branch.prefix.length
      }
      return limit
    },
    selectedBranch() {
      return this.branches.find(ter => ter.id === this.selectedBranchId)
    },
    branches() {
      return this.$store.getters['susuSavings/branches']
    },
  },
  watch: {
    modalOpened(opened) {
      this.branchModalOpened = opened
    },
    editId(id) {
      this.selectedBranchId = id
    },
  },
  methods: {
    resetBranchForm() {
      this.branch = {
        id: null,
        name: null,
        prefix: null,
        length: null,
      }
      this.selectedBranchId = null
      this.updateBranchMode = false
      this.$refs.branchForm.reset()
    },
    populateBranchForm(branch) {
      if (this.selectedBranch) {
        this.updateBranchMode = true
        this.branch = {
          id: this.selectedBranch.id,
          name: this.selectedBranch.name,
          prefix: this.selectedBranch.prefix,
          length: this.selectedBranch.length,
        }
      } else {
        this.resetBranchForm()
      }
    },
    closeBranchModal() {
      this.branchModalOpened = false
      this.$emit('modalClosed')
    },
    createBranch() {
      this.error = {}
      this.branchLoading = true
      this.$http.post('susu-savings/branches', this.branch).then(res => {
        this.showSuccess('Created branch successfully')
        this.$store.commit('susuSavings/addBranch', res.data)
        this.resetBranchForm()
      })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to create branch')
        }).finally(() => {
          this.branchLoading = false
        })
    },
    updateBranch() {
      this.error = {}
      if (!this.branch.id) {
        return
      }
      this.branchLoading = true
      this.$http.put(`susu-savings/branches/${this.branch.id}`, this.branch).then(res => {
        this.showSuccess('Updated branch successfully')
        this.$store.commit('susuSavings/updateBranch', res.data)
      })
        .catch(err => {
          logData(err)
          this.error = err
          this.showError('Failed to update branch')
        }).finally(() => {
          this.branchLoading = false
        })
    },
    deleteBranch() {
      if (!this.selectedBranchId) {
        this.showInfo('Please select a branch')
        return
      }
      this.deletingBranch = true
      this.$http.delete(`susu-savings/branches/${this.selectedBranchId}`).then(() => {
        this.showSuccess('Deleted Successfully')
        this.$store.commit('susuSavings/removeBranch', this.selectedBranchId)
        this.selectedBranchId = null
        this.resetBranchForm()
      })
        .catch(() => {
          this.showError('Failed to delete branch')
        }).finally(() => {
          this.deletingBranch = false
        })
    },
  }
  ,
}
</script>
